import { useState, useEffect } from 'react';
import { X, Save, FileText, Zap, Target, TrendingUp, AlertTriangle, Clock, Loader2, Calendar, Layers, Info } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { CollectionStrategyItem, StrategyAction, TriggerConfig, AssignmentRule } from '@/types/collection-strategies';
import { StrategyStageEditor } from '@/components/settings/strategy-stage-editor';
import { StageFunnelTable } from '@/components/collections/stage-funnel-table';
import { useStrategyDiagnostics } from '@/hooks/useStrategyDiagnosticsGraphQL';
import { cn } from '@/lib/utils';
import { useStrategyById, useCreateStrategy, useUpdateStrategy, useStrategyTypeOptions } from '@/hooks/useStrategiesData';
import { useCollectionStrategyAuditLogsGraphQL } from '@/hooks/useCollectionStrategyAuditLogsGraphQL';

interface InlineStrategyMode {
  mode: 'create' | 'edit';
  strategy?: CollectionStrategyItem;
  onBack: () => void;
  onSaved: (strategy: CollectionStrategyItem, action: 'create' | 'edit') => void;
}

interface StrategyEditorPageProps {
  inlineMode?: InlineStrategyMode;
}

function getAuditActionColor(actionType: string) {
    switch (actionType) {
        case 'StatusChanged':
            return 'bg-orange-500';
        case 'StrategyCreated':
            return 'bg-blue-500';
        case 'FieldChanged':
            return 'bg-yellow-500';
        case 'RunNowTriggered':
            return 'bg-purple-500';
        case 'ScheduledRunStarted':
            return 'bg-cyan-500';
        case 'ScheduledRunCompleted':
            return 'bg-green-500';
        case 'ScheduledRunFailed':
            return 'bg-red-500';
        default:
            return 'bg-gray-400';
    }
}

function formatActionType(actionType: string) {
    return actionType.replace(/([A-Z])/g, ' $1').trim();
}

function validateFormData(formData: { name: string; description: string; actions: { order: number; timing: { type: string } }[]; automationDays: Record<string, boolean>; automationTime: string }): boolean {
    if (!formData.name.trim()) {
        toast.error('Please enter a strategy name');
        return false;
    }
    if (!formData.description.trim()) {
        toast.error('Please enter a strategy description');
        return false;
    }
    if (formData.actions.length === 0) {
        toast.error('A strategy must have at least one stage');
        return false;
    }
    const sortedActions = [...formData.actions].sort((a, b) => a.order - b.order);
    if (sortedActions.length > 0 && sortedActions[0].timing.type !== 'days_from_trigger') {
        toast.error('Stage 1 must have a Timing Type of "Days from Trigger"');
        return false;
    }
    const hasAtLeastOneDay = Object.values(formData.automationDays).some(Boolean);
    if (!hasAtLeastOneDay) {
        toast.error('Please select at least one day for automation frequency');
        return false;
    }
    if (!formData.automationTime) {
        toast.error('Please set a time for automation frequency');
        return false;
    }
    return true;
}

export function StrategyEditorPage({ inlineMode }: StrategyEditorPageProps = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { strategyId: strategyIdParam } = useParams<{ strategyId: string }>();

    // Get strategy and mode from location state, URL params, or inlineMode
    const strategyFromState = inlineMode?.strategy ?? (location.state?.strategy as CollectionStrategyItem | null);
    const strategyId = inlineMode
      ? (inlineMode.strategy?.id ?? null)
      : (strategyIdParam || searchParams.get('id') || strategyFromState?.id || null);
    const mode = inlineMode?.mode ?? ((searchParams.get('mode') || location.state?.mode || (strategyId ? 'edit' : 'create')) as 'create' | 'edit');

    // Fetch strategy data from API when editing (for live data mode)
    // Pass strategyId directly (can be string or number)
    const { data: strategyFromApi, isLoading: isLoadingStrategy } = useStrategyById(strategyId);
    
    // Use API data if available, otherwise fall back to state data
    const strategy = strategyFromApi || strategyFromState;

    // Mutations for create and update
    const createStrategyMutation = useCreateStrategy({ forceLive: !!inlineMode });
    // For update, try to parse as number, otherwise use the string ID
    const strategyIdForUpdate = strategyId ? (isNaN(Number(strategyId)) ? strategyId : parseInt(strategyId, 10)) : 0;
    const updateStrategyMutation = useUpdateStrategy(strategyIdForUpdate, { forceLive: !!inlineMode });

    // Lookup data for dropdowns (cached for 1 minute)
    const { data: strategyTypeOptions = [] } = useStrategyTypeOptions();

    const [formData, setFormData] = useState({
        name: '',
        type: 'Standard' as CollectionStrategyItem['type'],
        description: '',
        status: 'Active' as 'Active' | 'Inactive',
        triggerType: 'days_overdue' as TriggerConfig['type'],
        daysOverdue: 0,
        amountThreshold: 5000,
        riskScoreThreshold: 70,
        actions: [] as StrategyAction[],
        assignmentRules: [] as AssignmentRule[],
        aiEnabled: false,
        aiTone: 3,
        aiFormality: 'professional' as 'casual' | 'professional' | 'formal',
        aiPersonalization: 'moderate' as 'minimal' | 'moderate' | 'deep',
        aiContextFactors: {
            paymentHistory: true,
            riskProfile: true,
            relationshipDuration: false,
            industryContext: false,
            economicFactors: false,
            previousDisputes: false,
            communicationPreferences: false,
        },
        aiTimingOptimization: true,
        // Automation Frequency fields
        automationTime: '09:00',
        automationDays: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: false,
            sunday: false,
        },
        multiStageHandling: 'RunAllInOrder' as string,
    });

    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [showActiveCasesWarningDialog, setShowActiveCasesWarningDialog] = useState(false);
    const [stagesChanged, setStagesChanged] = useState(false);
    
    // Get initial tab from URL parameter, default to 'performance' if coming from dashboard
    const initialTab = searchParams.get('tab') || 'settings';
    const [activeTab, setActiveTab] = useState(initialTab);
    const { data: diagnostics = null, isLoading: isLoadingDiagnostics } = useStrategyDiagnostics(
        mode === 'edit' ? strategyId : null
    );

    // Initialize form data when strategy changes
    useEffect(() => {
        if (strategy && mode === 'edit') {
            setFormData({
                name: strategy.name,
                type: strategy.type,
                description: strategy.description,
                status: strategy.status,
                triggerType: strategy.trigger.type,
                daysOverdue: strategy.trigger.daysOverdue ?? 0,
                amountThreshold: strategy.trigger.amountThreshold || 5000,
                riskScoreThreshold: strategy.trigger.riskScoreThreshold || 70,
                actions: strategy.actions,
                assignmentRules: strategy.assignmentRules,
                aiEnabled: !!strategy.aiBehavior?.enabled,
                aiTone: strategy.aiBehavior?.communicationTone || 3,
                aiFormality: strategy.aiBehavior?.formalityLevel || 'professional',
                aiPersonalization: strategy.aiBehavior?.personalizationDepth || 'moderate',
                aiContextFactors: strategy.aiBehavior?.contextFactors || {
                    paymentHistory: true,
                    riskProfile: true,
                    relationshipDuration: false,
                    industryContext: false,
                    economicFactors: false,
                    previousDisputes: false,
                    communicationPreferences: false,
                },
                aiTimingOptimization: strategy.aiBehavior?.timingOptimization || true,
                // Automation Frequency fields - use API values if present
                automationTime: strategy.automationTime ?? '09:00',
                automationDays: strategy.automationDays ?? {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: false,
                    sunday: false,
                },
                multiStageHandling: strategy.multiStageHandling ?? 'RunAllInOrder',
            });
            
            setStagesChanged(false);
        } else {
            setFormData({
                name: '',
                type: 'Standard',
                description: '',
                status: 'Active',
                triggerType: 'days_overdue',
                daysOverdue: 0,
                amountThreshold: 5000,
                riskScoreThreshold: 70,
                actions: [],
                assignmentRules: [],
                aiEnabled: false,
                aiTone: 3,
                aiFormality: 'professional',
                aiPersonalization: 'moderate',
                aiContextFactors: {
                    paymentHistory: true,
                    riskProfile: true,
                    relationshipDuration: false,
                    industryContext: false,
                    economicFactors: false,
                    previousDisputes: false,
                    communicationPreferences: false,
                },
                aiTimingOptimization: true,
                // Automation Frequency fields
                automationTime: '09:00',
                automationDays: {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: false,
                    sunday: false,
                },
                multiStageHandling: 'RunAllInOrder',
            });
        }
    }, [strategy, mode]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        if (mode === 'create') {
            return formData.name !== '' || formData.description !== '';
        }
        if (strategy) {
            return (
                formData.name !== strategy.name ||
                formData.type !== strategy.type ||
                formData.description !== strategy.description ||
                formData.status !== strategy.status ||
                formData.triggerType !== strategy.trigger.type ||
                formData.daysOverdue !== (strategy.trigger.daysOverdue ?? 0) ||
                formData.amountThreshold !== (strategy.trigger.amountThreshold || 5000) ||
                formData.riskScoreThreshold !== (strategy.trigger.riskScoreThreshold || 70)
            );
        }
        return false;
    };

    const performSave = async () => {
        if (!validateFormData(formData)) return;

        // Build the strategy object from form data
        const strategyData: CollectionStrategyItem = {
            id: strategy?.id || '0',
            name: formData.name,
            type: formData.type,
            description: formData.description,
            status: formData.status,
            trigger: {
                type: formData.triggerType,
                daysOverdue: formData.daysOverdue,
                amountThreshold: formData.amountThreshold,
                riskScoreThreshold: formData.riskScoreThreshold,
            },
            actions: formData.actions,
            assignmentRules: formData.assignmentRules,
            aiBehavior: formData.aiEnabled ? {
                enabled: formData.aiEnabled,
                communicationTone: formData.aiTone as 1 | 2 | 3 | 4 | 5,
                formalityLevel: formData.aiFormality,
                personalizationDepth: formData.aiPersonalization,
                contextFactors: formData.aiContextFactors,
                timingOptimization: formData.aiTimingOptimization,
                escalationTriggers: strategy?.aiBehavior?.escalationTriggers || {
                    noResponseDays: 7,
                    brokenPromises: 2,
                    disputeRaised: true,
                },
                humanReviewRequired: strategy?.aiBehavior?.humanReviewRequired ?? true,
            } : undefined,
            openCasesCount: strategy?.openCasesCount || 0,
            avgDaysOpen: strategy?.avgDaysOpen || 0,
            performanceMetrics: strategy?.performanceMetrics || {
                totalCasesProcessed: 0,
                successfulResolutions: 0,
                averageResolutionTime: 0,
                collectionRate: 0,
            },
            createdAt: strategy?.createdAt || new Date(),
            updatedAt: new Date(),
            automationTime: formData.automationTime,
            automationDays: formData.automationDays,
            multiStageHandling: formData.multiStageHandling,
        };

        try {
            if (mode === 'create') {
                await createStrategyMutation.mutateAsync(strategyData);
                toast.success('Strategy created successfully');
            } else {
                await updateStrategyMutation.mutateAsync(strategyData);
                toast.success('Strategy updated successfully');
            }
            if (inlineMode) {
                inlineMode.onSaved(strategyData as CollectionStrategyItem, mode);
            } else {
                navigate('/settings/strategies');
            }
        } catch (error: unknown) {
            const fallbackMessage = mode === 'create' ? 'Failed to create strategy' : 'Failed to update strategy';
            if (error instanceof Error && error.message) {
                toast.error(error.message);
            } else {
                toast.error(fallbackMessage);
            }
            console.error('Save strategy error:', error);
        }
    };

    const handleSave = async () => {
        // Basic validation
        if (!formData.name.trim()) {
            toast.error('Please enter a strategy name');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Please enter a strategy description');
            return;
        }

        // In edit mode, warn if there are active cases and changes have been made
        if (mode === 'edit' && (strategy?.activeCasesCount ?? 0) > 0 && (hasUnsavedChanges() || stagesChanged)) {
            setShowActiveCasesWarningDialog(true);
            return;
        }

        await performSave();
    };

    const handleCancel = () => {
        if (hasUnsavedChanges()) {
            setPendingNavigation('/settings/strategies');
            setShowUnsavedChangesDialog(true);
        } else {
            if (inlineMode) {
                inlineMode.onBack();
            } else {
                navigate('/settings/strategies');
            }
        }
    };

    const confirmNavigation = () => {
        if (inlineMode) {
            inlineMode.onBack();
        } else if (pendingNavigation) {
            navigate(pendingNavigation);
        }
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const cancelNavigation = () => {
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const handleActionsChange = (actions: StrategyAction[]) => {
        setFormData({
            ...formData,
            actions,
        });
    };

    // Audit log data from GraphQL
    const {
        auditLogs,
        isLoading: isLoadingAuditLogs,
        isFetchingMore: isFetchingMoreAuditLogs,
        hasMore: hasMoreAuditLogs,
        loadMore: loadMoreAuditLogs,
    } = useCollectionStrategyAuditLogsGraphQL(strategyId);

    const hasIssues = diagnostics && (diagnostics.emailsSent14Days === 0 || diagnostics.status === 'Inactive');
    const isActive = diagnostics?.status === 'Active';

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="shrink-0 border-b bg-background px-8 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            {mode === 'create' ? 'Create Strategy' : `Edit Strategy: ${strategy?.name}`}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {mode === 'create'
                                ? 'Create a new collection strategy with automated workflows'
                                : 'Modify the strategy settings and configuration'
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleCancel}>
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSave} 
                            disabled={!formData.name || !formData.description || createStrategyMutation.isPending || updateStrategyMutation.isPending || (isLoadingStrategy && mode === 'edit')}
                        >
                            {(createStrategyMutation.isPending || updateStrategyMutation.isPending) ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4 mr-2" />
                            )}
                            {mode === 'create' ? 'Create Strategy' : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content - Tabbed Layout */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                    <div className="shrink-0 border-b bg-background px-8">
                        <TabsList className="h-12">
                            <TabsTrigger value="settings" className="gap-2">
                                <FileText className="h-4 w-4" />
                                Settings
                            </TabsTrigger>
                            {mode === 'edit' && (
                                <TabsTrigger value="performance" className="gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    Performance
                                </TabsTrigger>
                            )}
                            {mode === 'edit' && (
                                <TabsTrigger value="audit-log" className="gap-2">
                                    <Clock className="h-4 w-4" />
                                    Audit Log
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>

                    {/* Settings Tab */}
                    <TabsContent value="settings" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                        {isLoadingStrategy && mode === 'edit' ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <span className="ml-2 text-muted-foreground">Loading strategy...</span>
                            </div>
                        ) : (
                        <div className="max-w-7xl mx-auto space-y-6">
                    {/* Row 1: Overview and Triggers Side-by-Side */}
                    <div className="grid grid-cols-2 gap-6">
                        {/* Panel 1: Overview */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-blue-500" />
                                    Overview
                                </CardTitle>
                                <CardDescription>
                                    Basic information about the collection strategy
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Strategy name *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Standard 30-Day Collection"
                                    />
                                </div>
                                <div>
                                    <Label>Type *</Label>
                                    <Select
                                        value={formData.type}
                                        onValueChange={(value: CollectionStrategyItem['type']) => setFormData({ ...formData, type: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {strategyTypeOptions.length > 0 ? (
                                                strategyTypeOptions.map((option) => (
                                                    <SelectItem key={option.id} value={option.name}>
                                                        {option.name}
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                <>
                                                    <SelectItem value="Standard">Standard</SelectItem>
                                                    <SelectItem value="Intensive">Intensive</SelectItem>
                                                    <SelectItem value="VIP">VIP</SelectItem>
                                                    <SelectItem value="High Risk">High Risk</SelectItem>
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Description *</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Describe when and how this strategy should be used..."
                                        rows={4}
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Label>Multi-Stage Handling</Label>
                                        <TooltipProvider delayDuration={300}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Info className="h-4 w-4 text-muted-foreground cursor-help flex-shrink-0" />
                                                </TooltipTrigger>
                                                <TooltipContent side="right" className="max-w-xs text-xs">
                                                    <p className="font-medium mb-1">Controls how stages are processed when multiple stages become eligible in a single strategy run.</p>
                                                    <p className="mb-0.5"><strong>Run All in Order</strong> — Every triggered stage is processed one after another in sequence.</p>
                                                    <p className="mb-0.5"><strong>Skip to Latest</strong> — Only the most recent stage is processed; earlier stages are skipped.</p>
                                                    <p><strong>One Per Run</strong> — Only one stage is processed per run. Remaining stages are picked up in subsequent runs.</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <Select
                                        value={formData.multiStageHandling}
                                        onValueChange={(value: string) => setFormData({ ...formData, multiStageHandling: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="RunAllInOrder">Run All in Order</SelectItem>
                                            <SelectItem value="SkipToLatest">Skip to Latest</SelectItem>
                                            <SelectItem value="OnePerRun">One Per Run</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Panel 2: Triggers */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Zap className="h-5 w-5 text-amber-500" />
                                    Triggers
                                </CardTitle>
                                <CardDescription>
                                    Define when this strategy should be automatically applied
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Trigger Type</Label>
                                    <Select
                                        value={formData.triggerType}
                                        onValueChange={(value: 'days_overdue' | 'amount_threshold' | 'risk_score' | 'manual_only' | 'payment_pattern_change' | 'promise_broken') => setFormData({ ...formData, triggerType: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="days_overdue">Days Overdue</SelectItem>
                                            <SelectItem value="amount_threshold">Amount Threshold</SelectItem>
                                            {/* <SelectItem value="risk_score">Risk Score</SelectItem> */}
                                            {/* <SelectItem value="manual_only">Manual Only</SelectItem> */}
                                            {/* <SelectItem value="payment_pattern_change">Payment Pattern Change</SelectItem> */}
                                            {/* <SelectItem value="promise_broken">Promise Broken</SelectItem> */}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {formData.triggerType === 'days_overdue' && (
                                    <div>
                                        <Label>Days Overdue Threshold</Label>
                                        <Input
                                            type="number"
                                            value={formData.daysOverdue}
                                            onChange={(e) => setFormData({ ...formData, daysOverdue: parseInt(e.target.value) || 0 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when invoice is overdue by this many days
                                        </p>
                                    </div>
                                )}

                                {formData.triggerType === 'amount_threshold' && (
                                    <div>
                                        <Label>Amount Threshold (£)</Label>
                                        <Input
                                            type="number"
                                            value={formData.amountThreshold}
                                            onChange={(e) => setFormData({ ...formData, amountThreshold: parseInt(e.target.value) || 0 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when outstanding amount exceeds this value
                                        </p>
                                    </div>
                                )}

                                {formData.triggerType === 'risk_score' && (
                                    <div>
                                        <Label>Risk Score Threshold</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={formData.riskScoreThreshold}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 0;
                                                setFormData({ ...formData, riskScoreThreshold: Math.min(100, Math.max(0, value)) });
                                            }}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when customer risk score reaches or exceeds this value (0-100)
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Panel 3: Stages */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-green-500" />
                                Stages
                            </CardTitle>
                            <CardDescription>
                                Configure the collection actions to be performed
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <StrategyStageEditor
                                actions={formData.actions}
                                onActionsChange={handleActionsChange}
                            />
                        </CardContent>
                    </Card>

                    {/* Panel 4: Automation Frequency */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-purple-500" />
                                Automation Frequency
                            </CardTitle>
                            <CardDescription>
                                Configure when this strategy should run automatically
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Field 1: Time */}
                            <div>
                                <Label>Time *</Label>
                                <Select
                                    value={formData.automationTime}
                                    onValueChange={(value: string) => setFormData({ ...formData, automationTime: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="00:00">12:00 AM</SelectItem>
                                        <SelectItem value="01:00">1:00 AM</SelectItem>
                                        <SelectItem value="02:00">2:00 AM</SelectItem>
                                        <SelectItem value="03:00">3:00 AM</SelectItem>
                                        <SelectItem value="04:00">4:00 AM</SelectItem>
                                        <SelectItem value="05:00">5:00 AM</SelectItem>
                                        <SelectItem value="06:00">6:00 AM</SelectItem>
                                        <SelectItem value="07:00">7:00 AM</SelectItem>
                                        <SelectItem value="08:00">8:00 AM</SelectItem>
                                        <SelectItem value="09:00">9:00 AM</SelectItem>
                                        <SelectItem value="10:00">10:00 AM</SelectItem>
                                        <SelectItem value="11:00">11:00 AM</SelectItem>
                                        <SelectItem value="12:00">12:00 PM</SelectItem>
                                        <SelectItem value="13:00">1:00 PM</SelectItem>
                                        <SelectItem value="14:00">2:00 PM</SelectItem>
                                        <SelectItem value="15:00">3:00 PM</SelectItem>
                                        <SelectItem value="16:00">4:00 PM</SelectItem>
                                        <SelectItem value="17:00">5:00 PM</SelectItem>
                                        <SelectItem value="18:00">6:00 PM</SelectItem>
                                        <SelectItem value="19:00">7:00 PM</SelectItem>
                                        <SelectItem value="20:00">8:00 PM</SelectItem>
                                        <SelectItem value="21:00">9:00 PM</SelectItem>
                                        <SelectItem value="22:00">10:00 PM</SelectItem>
                                        <SelectItem value="23:00">11:00 PM</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Select the time for automation execution
                                </p>
                            </div>

                            {/* Field 2: Days of the Week */}
                            <div>
                                <Label>Days of the Week *</Label>
                                <div className="mt-2 space-y-2">
                                    {[
                                        { key: 'monday', label: 'Monday' },
                                        { key: 'tuesday', label: 'Tuesday' },
                                        { key: 'wednesday', label: 'Wednesday' },
                                        { key: 'thursday', label: 'Thursday' },
                                        { key: 'friday', label: 'Friday' },
                                        { key: 'saturday', label: 'Saturday' },
                                        { key: 'sunday', label: 'Sunday' },
                                    ].map((day) => (
                                        <div key={day.key} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={day.key}
                                                checked={formData.automationDays[day.key as keyof typeof formData.automationDays]}
                                                onCheckedChange={(checked) => {
                                                    setFormData({
                                                        ...formData,
                                                        automationDays: {
                                                            ...formData.automationDays,
                                                            [day.key]: checked === true,
                                                        },
                                                    });
                                                }}
                                            />
                                            <label
                                                htmlFor={day.key}
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                            >
                                                {day.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Select which days the strategy should run (at least one required)
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                        </div>
                        )}
                    </TabsContent>

                    {/* Performance Tab */}
                    {mode === 'edit' && (
                        <TabsContent value="performance" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                            {isLoadingDiagnostics ? (
                                <div className="max-w-7xl mx-auto flex items-center justify-center py-16">
                                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    <span className="ml-2 text-muted-foreground">Loading performance data...</span>
                                </div>
                            ) : !diagnostics ? (
                                <div className="max-w-7xl mx-auto flex flex-col items-center justify-center py-16">
                                    <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
                                    <h3 className="text-lg font-semibold text-muted-foreground mb-2">No Performance Data Available</h3>
                                    <p className="text-sm text-muted-foreground text-center max-w-md">
                                        Performance diagnostics will appear here once the strategy has been run and data has been collected.
                                    </p>
                                </div>
                            ) : (
                            <div className="max-w-7xl mx-auto space-y-6">
                                {/* Warning Banner */}
                                {hasIssues && (
                                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <h4 className="text-base font-semibold text-amber-900 mb-1">
                                                    Strategy Issue Detected
                                                </h4>
                                                <p className="text-sm text-amber-800 mb-2">
                                                    {diagnostics.impactAnalysis.notReceivingEmails
                                                        ? `${diagnostics.impactAnalysis.notReceivingEmails.count} customers did not receive follow-up emails`
                                                        : 'This strategy requires attention'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Status Overview Card — full width */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Target className="h-5 w-5 text-teal-600" />
                                            Status Overview
                                        </CardTitle>
                                        <CardDescription>
                                            Current status and key metrics
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-5 gap-x-4">
                                            {/* Created */}
                                            <div className="space-y-0.5">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {format(diagnostics.createdAt, 'dd MMM yyyy')}
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    by {diagnostics.createdBy.name}
                                                </p>
                                            </div>

                                            {/* Open Cases */}
                                            <div className="space-y-0.5">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Open Cases</p>
                                                <div className="flex items-center gap-2">
                                                    <Target className="h-5 w-5 text-teal-600" />
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        {diagnostics.activeAccounts}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Last Run */}
                                            <div className="space-y-0.5">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Last Run</p>
                                                {diagnostics.lastRun ? (
                                                    <>
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            {format(diagnostics.lastRun, 'dd MMM yyyy, h:mmaaa')}
                                                        </p>
                                                        <p className="text-xs text-gray-600">
                                                            {formatDistanceToNow(diagnostics.lastRun, { addSuffix: true })}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <p className="text-sm text-gray-400">—</p>
                                                )}
                                            </div>

                                            {/* Emails Sent */}
                                            <div className="space-y-0.5">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Emails Sent (14 Days)</p>
                                                <div className="flex items-center gap-2">
                                                    {diagnostics.emailsSent14Days === 0 && (
                                                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                                                    )}
                                                    <p className={cn(
                                                        'text-2xl font-bold',
                                                        diagnostics.emailsSent14Days === 0 ? 'text-red-600' : 'text-gray-900'
                                                    )}>
                                                        {diagnostics.emailsSent14Days}
                                                    </p>
                                                </div>
                                                {diagnostics.expectedEmails && (
                                                    <p className="text-xs text-gray-600">
                                                        Expected: ~{diagnostics.expectedEmails}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Next Scheduled Run */}
                                            <div className="space-y-0.5">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Next Scheduled Run</p>
                                                {diagnostics.nextScheduledRun && isActive ? (
                                                    <>
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            {format(diagnostics.nextScheduledRun, 'dd MMM yyyy, h:mmaaa')}
                                                        </p>
                                                        <p className="text-xs text-green-600">
                                                            {formatDistanceToNow(diagnostics.nextScheduledRun, { addSuffix: true })}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <p className="text-sm text-gray-400">—</p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Stage Breakdown Card — full width below Status Overview */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Layers className="h-5 w-5 text-teal-600" />
                                            Stage Breakdown
                                        </CardTitle>
                                        <CardDescription>
                                            Number of cases at each stage of this strategy
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <StageFunnelTable data={diagnostics.stageFunnel} />
                                    </CardContent>
                                </Card>

                            </div>
                            )}
                        </TabsContent>
                    )}

                    {/* Audit Log Tab */}
                    {mode === 'edit' && (
                        <TabsContent value="audit-log" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                            <div className="max-w-7xl mx-auto space-y-6">
                                {/* Audit Trail Card */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Clock className="h-5 w-5 text-blue-600" />
                                            Audit Trail
                                        </CardTitle>
                                        <CardDescription>
                                            Recent changes and actions
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {isLoadingAuditLogs ? (
                                            <div className="flex items-center justify-center py-8">
                                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                                <span className="ml-2 text-muted-foreground">Loading audit logs...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Date & Time</TableHead>
                                                            <TableHead>Action</TableHead>
                                                            <TableHead>Description</TableHead>
                                                            <TableHead>Field</TableHead>
                                                            <TableHead>Old Value</TableHead>
                                                            <TableHead>New Value</TableHead>
                                                            <TableHead>User</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {auditLogs.length === 0 ? (
                                                            <TableRow>
                                                                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                                                    No audit log entries found
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (
                                                            auditLogs.map((log) => (
                                                                <TableRow key={log.id}>
                                                                    <TableCell className="whitespace-nowrap">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-sm font-medium text-gray-900">
                                                                                {format(log.createdAt, 'dd MMM yyyy')}
                                                                            </span>
                                                                            <span className="text-xs text-gray-500">
                                                                                {format(log.createdAt, 'h:mm aaa')}
                                                                            </span>
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <div className="flex items-center gap-2">
                                                                            <div
                                                                                className={cn(
                                                                                    'w-2 h-2 rounded-full flex-shrink-0',
                                                                                    getAuditActionColor(log.actionType)
                                                                                )}
                                                                            />
                                                                            <span className="text-sm font-medium text-gray-900">
                                                                                {formatActionType(log.actionType)}
                                                                            </span>
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <span className="text-sm text-gray-600 break-words">
                                                                            {log.description}
                                                                        </span>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {log.fieldName ? (
                                                                            <span className="text-sm text-gray-900">{log.fieldName}</span>
                                                                        ) : (
                                                                            <span className="text-sm text-gray-400">—</span>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {log.oldValue ? (
                                                                            <span className="text-sm text-gray-600 break-words">{log.oldValue}</span>
                                                                        ) : (
                                                                            <span className="text-sm text-gray-400">—</span>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {log.newValue ? (
                                                                            <span className="text-sm text-gray-600 break-words">{log.newValue}</span>
                                                                        ) : (
                                                                            <span className="text-sm text-gray-400">—</span>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="whitespace-nowrap">
                                                                        <span className="text-sm text-gray-900">
                                                                            {log.createdBy}
                                                                        </span>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        )}
                                                    </TableBody>
                                                </Table>
                                                {hasMoreAuditLogs && (
                                                    <div className="flex justify-center pt-4">
                                                        <Button
                                                            variant="outline"
                                                            onClick={loadMoreAuditLogs}
                                                            disabled={isFetchingMoreAuditLogs}
                                                        >
                                                            {isFetchingMoreAuditLogs ? (
                                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                            ) : null}
                                                            Load More
                                                        </Button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                    )}
                </Tabs>

            {/* Unsaved Changes Dialog */}
            <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelNavigation}>Stay on Page</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmNavigation}>Leave Without Saving</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Active Cases Warning Dialog */}
            <AlertDialog open={showActiveCasesWarningDialog} onOpenChange={setShowActiveCasesWarningDialog}>
                <AlertDialogContent className="max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Save Changes to Active Strategy?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm text-foreground">
                                <p>
                                    This strategy has{' '}
                                    <span className="font-semibold">
                                        {strategy?.activeCasesCount} active case{strategy?.activeCasesCount === 1 ? '' : 's'}
                                    </span>{' '}
                                    that are currently being processed.
                                </p>
                                <p>
                                    Saving these stage changes will affect what happens next for all cases that haven't yet completed the amended stages — they will follow the new stage configuration from their current position onwards. Completed stages are not affected.
                                </p>
                                <p className="font-medium">Are you sure you want to continue?</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowActiveCasesWarningDialog(false)}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => {
                                setShowActiveCasesWarningDialog(false);
                                await performSave();
                            }}
                        >
                            Yes, Save Changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default StrategyEditorPage;
